/**
 * Form validation utility functions
 * Provides reusable validators for common form fields
 */

/**
 * Validates an email address format
 * @param {string} email - The email to validate
 * @returns {boolean} True if valid, false otherwise
 */
export const isValidEmail = (email) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

/**
 * Validates a phone number format
 * Accepts digits, spaces, plus, minus, and parentheses (minimum 6 characters)
 * @param {string} phone - The phone number to validate
 * @returns {boolean} True if valid, false otherwise
 */
export const isValidPhone = (phone) => {
  return /^[\d\s+\-()]{6,}$/.test(phone);
};

/**
 * Validates text has minimum length after trimming
 * @param {string} text - The text to validate
 * @param {number} minLength - Minimum required length (default: 2)
 * @returns {boolean} True if valid, false otherwise
 */
export const hasMinLength = (text, minLength = 2) => {
  return text.trim().length >= minLength;
};

/**
 * Validates text has maximum length
 * @param {string} text - The text to validate
 * @param {number} maxLength - Maximum allowed length
 * @returns {boolean} True if valid, false otherwise
 */
export const hasMaxLength = (text, maxLength) => {
  return text.length <= maxLength;
};

/**
 * Validates a required field is not empty
 * @param {string} value - The value to validate
 * @returns {boolean} True if not empty, false otherwise
 */
export const isRequired = (value) => {
  return value !== null && value !== undefined && value.trim().length > 0;
};

/**
 * Validates a URL format
 * @param {string} url - The URL to validate
 * @returns {boolean} True if valid, false otherwise
 */
export const isValidUrl = (url) => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

/**
 * Validates a number is within a range
 * @param {number} value - The number to validate
 * @param {number} min - Minimum value (inclusive)
 * @param {number} max - Maximum value (inclusive)
 * @returns {boolean} True if valid, false otherwise
 */
export const isInRange = (value, min, max) => {
  const num = Number(value);
  return !isNaN(num) && num >= min && num <= max;
};

/**
 * Validates a postal code format (flexible international format)
 * @param {string} postalCode - The postal code to validate
 * @returns {boolean} True if valid, false otherwise
 */
export const isValidPostalCode = (postalCode) => {
  return /^[A-Z0-9\s-]{3,10}$/i.test(postalCode);
};

/**
 * Pre-built validation configurations for common form fields
 */
export const validationRules = {
  name: {
    validator: (value) => hasMinLength(value, 2),
    errorKey: 'errors.nameRequired',
  },
  email: {
    validator: isValidEmail,
    errorKey: 'errors.emailInvalid',
  },
  phone: {
    validator: isValidPhone,
    errorKey: 'errors.phoneInvalid',
  },
  message: {
    validator: (value) => hasMinLength(value, 10),
    errorKey: 'errors.messageRequired',
  },
};

/**
 * Generic form validator that applies rules to form data
 * @param {Object} formData - The form data to validate
 * @param {Object} rules - Validation rules object
 * @param {Function} t - Translation function for error messages
 * @returns {Object} Object containing errors for invalid fields
 *
 * @example
 * const errors = validateFormData(
 *   { name: 'John', email: 'invalid' },
 *   { name: validationRules.name, email: validationRules.email },
 *   t
 * );
 */
export const validateFormData = (formData, rules, t) => {
  const errors = {};

  Object.keys(rules).forEach((field) => {
    const rule = rules[field];
    const value = formData[field];

    if (!rule.validator(value)) {
      errors[field] = t(rule.errorKey);
    }
  });

  return errors;
};
